import {
  ComponentProps,
  forwardRef,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react';
import {
  BaseSelect,
  cn,
  Loader,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@sib/ui';

export interface SelectOption {
  label: string | ReactNode;
  Icon?: ReactNode;
  triggerLabel?: string;
  value: string | number | null;
  disabled?: boolean;
  hidden?: boolean;
}

export type SelectProps = ComponentProps<typeof BaseSelect> & {
  options: SelectOption[];
  placeholder?: string;
  selectTriggerProps?: ComponentProps<typeof SelectTrigger>;
  selectValueProps?: ComponentProps<typeof SelectValue>;
  selectContentProps?: ComponentProps<typeof SelectContent>;
  selectItemProps?: Omit<ComponentProps<typeof SelectItem>, 'value'>;
  loading?: boolean;
  clearable?: boolean;
  onItemSelect?: (value: string, index: number) => void;
};

const Select = forwardRef<HTMLSpanElement, SelectProps>(
  (
    {
      options,
      selectContentProps,
      selectItemProps,
      selectTriggerProps,
      selectValueProps,
      placeholder,
      loading,
      clearable,
      onItemSelect,
      ...props
    },
    ref
  ) => {
    const [open, setOpen] = useState(props.open);

    const selectedValue = useMemo(() => {
      const selected = options.find(({ value }) => value === props.value);

      const value = selected?.triggerLabel || selected?.label || null;

      return (
        <span className="inline-flex items-center gap-2">
          {selected?.Icon}
          {value}
        </span>
      );
    }, [options, props.value]);

    const handleItemPointerDown = useCallback(
      (e: React.PointerEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (props.open === undefined) setOpen(false);

        props.onValueChange?.('');
      },
      [props]
    );

    return (
      <BaseSelect
        {...props}
        {...(props.open === undefined && {
          open,
          onOpenChange: value => {
            setOpen(value);
            props.onOpenChange?.(value);
          },
        })}
      >
        <SelectTrigger
          {...selectTriggerProps}
          className={cn(
            selectTriggerProps?.className,
            '[&>span]:w-[calc(100%-16px)] [&>span]:text-left'
          )}
          disabled={props.disabled || loading}
          {...(loading && {
            icon: <Loader />,
          })}
        >
          <SelectValue
            ref={ref}
            placeholder={placeholder}
            {...selectValueProps}
          >
            {selectedValue}
            {selectTriggerProps?.children}
          </SelectValue>
        </SelectTrigger>
        {options.length > 0 && (props?.open || open) && (
          <SelectContent {...selectContentProps}>
            {options.map(({ value, label, disabled, hidden, Icon }, index) => {
              const isSelected = value === props.value;

              return (
                <SelectItem
                  {...selectItemProps}
                  value={value as string}
                  key={`select-item-${index}-${value}`}
                  className={cn(
                    disabled && 'text-muted-foreground pointer-events-none',
                    hidden && 'hidden',
                    selectItemProps?.className
                  )}
                  {...(clearable &&
                    isSelected && {
                      onPointerDown: handleItemPointerDown,
                    })}
                  {...(onItemSelect && {
                    onPointerDown: (e: React.PointerEvent) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onItemSelect(value as string, index);
                    },
                  })}
                >
                  <div className="flex flex-row items-center gap-2">
                    {Icon}
                    {label}
                  </div>
                </SelectItem>
              );
            })}
            {selectContentProps?.children}
          </SelectContent>
        )}
      </BaseSelect>
    );
  }
);

export { Select };
